import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Icon } from "../../icons/Icon";
import { Carregando } from "../../components/Layout";
import { api, fmt } from "../../lib/api";
import type { Sessao } from "./Admin";

type Linha = {
  id: number; token: string; nome: string; cpfMascarado: string; cargo: string;
  componente: string | null; modalidade: string; status: string;
  pontosTotal: number; pontosQualificacao: number; pontosExperiencia: number;
  enviadaEm: string; analisadaPor: string | null;
};

type Pagina = {
  conteudo: Linha[]; pagina: number; tamanho: number;
  totalElementos: number; totalPaginas: number;
};

type Cargo = { id: number; codigo: string; nome: string };

const STATUS = ["RECEBIDA", "EM_ANALISE", "DEFERIDA", "INDEFERIDA", "CLASSIFICADA",
  "CONVOCADA", "CONTRATADA", "ELIMINADA"];

/**
 * Fila de análise — a tela onde a comissão passa mais tempo.
 *
 * Densidade alta de propósito: linhas compactas, numerais tabulares e
 * hairlines em vez de cartões, para que a analista veja muitos candidatos de
 * uma vez e compare pontuações com o olho, sem rolar.
 */
export function FilaInscricoes({ sessao }: { sessao: Sessao }) {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const modalidade = params.get("modalidade") ?? "";
  const cargoId = params.get("cargoId") ?? "";
  const busca = params.get("busca") ?? "";
  const pagina = Number(params.get("pagina") ?? "0");

  const [dados, setDados] = useState<Pagina | null>(null);
  const [cargos, setCargos] = useState<Cargo[]>([]);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    api.get<{ cargos: Cargo[] }>("/api/publico/edital")
      .then((e) => setCargos(e.cargos))
      .catch(() => setCargos([]));
  }, []);

  function atualizarFiltro(mudanca: Record<string, string>) {
    const novo = new URLSearchParams(params);
    for (const [chave, valor] of Object.entries(mudanca)) {
      if (valor) novo.set(chave, valor); else novo.delete(chave);
    }
    if (!("pagina" in mudanca)) novo.delete("pagina");
    setParams(novo);
  }

  useEffect(() => {
    setCarregando(true);
    const q = new URLSearchParams({ pagina: String(pagina), tamanho: "25" });
    if (status) q.set("status", status);
    if (modalidade) q.set("modalidade", modalidade);
    if (cargoId) q.set("cargoId", cargoId);
    if (busca.trim()) q.set("busca", busca.trim());
    api.get<Pagina>(`/api/admin/inscricoes?${q}`)
      .then(setDados)
      .catch(() => setDados(null))
      .finally(() => setCarregando(false));
  }, [status, modalidade, cargoId, busca, pagina]);

  return (
    <div className="pilha pilha-6">
      <div className="linha linha-entre linha-quebra">
        <div>
          <h1>Inscrições</h1>
          <p className="texto-secundario">
            {dados ? `${dados.totalElementos} inscrição(ões)` : "Carregando"} ·{" "}
            perfil {sessao.perfil?.toLowerCase()}
          </p>
        </div>
      </div>

      <div className="cartao filtros">
        <div className="campo">
          <label className="campo-rotulo" htmlFor="f-busca">Buscar</label>
          <input id="f-busca" className="entrada" placeholder="Nome, token ou CPF"
                 value={busca}
                 onChange={(e) => atualizarFiltro({ busca: e.target.value })} />
        </div>
        <div className="campo">
          <label className="campo-rotulo" htmlFor="f-status">Situação</label>
          <select id="f-status" className="selecao" value={status}
                  onChange={(e) => atualizarFiltro({ status: e.target.value })}>
            <option value="">Todas</option>
            {STATUS.map((s) => <option key={s} value={s}>{s.replace("_", " ").toLowerCase()}</option>)}
          </select>
        </div>
        <div className="campo">
          <label className="campo-rotulo" htmlFor="f-mod">Modalidade</label>
          <select id="f-mod" className="selecao" value={modalidade}
                  onChange={(e) => atualizarFiltro({ modalidade: e.target.value })}>
            <option value="">Todas</option>
            <option value="AMPLA">Ampla concorrência</option>
            <option value="PCD">Pessoa com Deficiência</option>
            <option value="NEGRO">Vaga reservada a negros</option>
          </select>
        </div>
        <div className="campo">
          <label className="campo-rotulo" htmlFor="f-cargo">Vaga</label>
          <select id="f-cargo" className="selecao" value={cargoId}
                  onChange={(e) => atualizarFiltro({ cargoId: e.target.value })}>
            <option value="">Todas</option>
            {cargos.map((c) => <option key={c.id} value={c.id}>{c.codigo}</option>)}
          </select>
        </div>
      </div>

      {carregando && <Carregando texto="Carregando inscrições" />}

      {!carregando && dados && (
        <div className="painel">
          <div className="tabela-rolagem">
            <table className="tabela">
              <caption className="so-leitor">
                Fila de inscrições para análise, ordenada por pontuação total.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Candidato</th>
                  <th scope="col">Cargo</th>
                  <th scope="col">Modalidade</th>
                  <th scope="col" className="col-num">Total</th>
                  <th scope="col" className="col-num">Qualif.</th>
                  <th scope="col" className="col-num">Exper.</th>
                  <th scope="col">Situação</th>
                  <th scope="col"><span className="so-leitor">Ações</span></th>
                </tr>
              </thead>
              <tbody>
                {dados.conteudo.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <strong>{l.nome}</strong>
                      <br />
                      <span className="texto-sutil mono">{l.token}</span>
                      <span className="texto-sutil"> · {l.cpfMascarado}</span>
                    </td>
                    <td>
                      {l.cargo}
                      {l.componente && <><br /><span className="texto-sutil">{l.componente}</span></>}
                    </td>
                    <td>
                      {l.modalidade === "PCD" && <span className="etiqueta etiqueta-pcd">PcD</span>}
                      {l.modalidade === "NEGRO" && <span className="etiqueta etiqueta-negro">Negro</span>}
                      {l.modalidade === "AMPLA" && <span className="texto-sutil">Ampla</span>}
                    </td>
                    <td className="col-num"><strong>{fmt.pontos(l.pontosTotal)}</strong></td>
                    <td className="col-num">{fmt.pontos(l.pontosQualificacao)}</td>
                    <td className="col-num">{fmt.pontos(l.pontosExperiencia)}</td>
                    <td>
                      <span className={`situacao situacao-${l.status}`}>
                        {l.status.replace("_", " ").toLowerCase()}
                      </span>
                    </td>
                    <td>
                      <Link to={`/admin/inscricoes/${l.id}`} className="btn btn-sutil btn-pequeno">
                        Analisar
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {dados.conteudo.length === 0 && (
            <p className="vazio">Nenhuma inscrição corresponde aos filtros.</p>
          )}

          {dados.totalPaginas > 1 && (
            <div className="paginacao">
              <button className="btn btn-secundario btn-pequeno" disabled={pagina === 0}
                      onClick={() => atualizarFiltro({ pagina: String(pagina - 1) })}>
                <Icon nome="esquerda" tamanho={14} />
                Anterior
              </button>
              <span className="texto-sutil num">
                Página {dados.pagina + 1} de {dados.totalPaginas}
              </span>
              <button className="btn btn-secundario btn-pequeno"
                      disabled={pagina >= dados.totalPaginas - 1}
                      onClick={() => atualizarFiltro({ pagina: String(pagina + 1) })}>
                Próxima
                <Icon nome="direita" tamanho={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
